/**
 * ARForm Zod Validation Schema
 * Validates all 26 form fields (17 required, 9 optional)
 */

import { z } from 'zod'

// ============================================================================
// Enum Schemas
// ============================================================================

export const hazardSeveritySchema = z.enum(['minor', 'moderate', 'severe'])

export const postingStatusSchema = z.enum(['inspected', 'restricted_use', 'unsafe'])

export const damagePercentageSchema = z.enum(['none', '0_1', '1_10', '10_30', '30_60', '60_100', '100'])

export const areasInspectedSchema = z.enum(['exterior_only', 'exterior_and_interior'])

export const constructionTypeSchema = z.enum([
  'wood_frame',
  'steel_frame',
  'concrete_frame',
  'concrete_shear_wall',
  'unreinforced_masonry',
  'reinforced_masonry',
  'tilt_up',
  'other',
])

export const occupancyTypeSchema = z.enum([
  'residential',
  'other_residential',
  'commercial',
  'offices',
  'industrial',
  'government',
  'school',
  'hospital',
  'historic',
  'public_assembly',
  'other',
])

export const detailedEvalTypeSchema = z.enum(['structural', 'geotechnical', 'other'])

// ============================================================================
// Complex Object Schemas
// ============================================================================

/**
 * Hazard assessment schema
 * 5 core hazards are required, "other" is optional
 */
export const hazardAssessmentSchema = z
  .object({
    collapseOrOffFoundation: hazardSeveritySchema,
    buildingOrStoryLeaning: hazardSeveritySchema,
    rackingDamageWalls: hazardSeveritySchema,
    chimneyParapetFallingHazard: hazardSeveritySchema,
    groundSlopeMovement: hazardSeveritySchema,
    otherDescription: z.string().optional(),
    otherSeverity: hazardSeveritySchema.optional(),
  })
  .refine(
    (data) => {
      // If otherDescription is provided, otherSeverity should also be provided
      if (data.otherDescription && data.otherDescription.trim() !== '') {
        return data.otherSeverity !== undefined
      }
      return true
    },
    {
      message: 'Severity is required when "Other" hazard description is provided',
      path: ['otherSeverity'],
    }
  )

/**
 * Assessment photo schema
 */
export const assessmentPhotoSchema = z.object({
  id: z.string(),
  uri: z.string(),
  timestamp: z.string(),
  caption: z.string().optional(),
})

// ============================================================================
// Main Form Schema
// ============================================================================

/**
 * Complete ARForm validation schema
 * 26 fields: 17 required, 9 optional
 */
export const arFormSchema = z.object({
  // ============================================
  // Metadata
  // ============================================
  id: z.string().optional(),
  parcelId: z.string(),

  // ============================================
  // Section 1: Inspection (4 fields)
  // ============================================
  inspectorId: z.string().min(1, 'Inspector ID is required'),
  affiliation: z.string().optional(),
  inspectionDateTime: z.string().min(1, 'Inspection date/time is required'),
  areasInspected: areasInspectedSchema,

  // ============================================
  // Section 2: Building (10 fields)
  // ============================================
  address1: z.string().min(1, 'Address is required'),
  address2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  county: z.string().min(1, 'County is required'),
  state: z.string().min(1, 'State is required'),
  zipcode: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  typeOfConstruction: constructionTypeSchema,
  primaryOccupancy: occupancyTypeSchema,
  contactPhone: z.string().optional(),
  storiesAboveGround: z.string().optional(),
  storiesBelowGround: z.string().optional(),
  buildingFootprint: z.string().optional(),
  residentialUnits: z.string().optional(),
  inhabitableResidentialUnits: z.string().optional(),

  // ============================================
  // Section 3: Evaluation (3 fields)
  // ============================================
  hazards: hazardAssessmentSchema,
  estimatedBuildingDamage: damagePercentageSchema,
  comments: z.string().optional(),

  // ============================================
  // Section 4: Posting (4 fields)
  // ============================================
  postingStatus: postingStatusSchema.optional(),
  restrictionsText: z.string().optional(),
  detailedEvalRecommended: z.boolean(),
  detailedEvalTypes: z.array(detailedEvalTypeSchema).optional(),

  // ============================================
  // Section 5: Further Actions
  // ============================================
  barricadesNeeded: z.boolean().optional(),
  barricadeLocations: z.string().optional(),
  engineeringEvalRecommended: z.boolean().optional(),
  engineeringEvalStructural: z.boolean().optional(),
  engineeringEvalGeotechnical: z.boolean().optional(),
  engineeringEvalOther: z.string().optional(),
  actionsComments: z.string().optional(),

  // ============================================
  // Section 6: Image (1 field)
  // ============================================
  photos: z.array(assessmentPhotoSchema).optional(),
})

/**
 * Inferred TypeScript type from the schema
 */
export type ARFormSchemaType = z.infer<typeof arFormSchema>

// ============================================================================
// Section-Specific Validation Schemas (for per-section validation if needed)
// ============================================================================

/**
 * Inspection section validation
 */
export const inspectionSectionSchema = z.object({
  inspectorId: z.string().min(1, 'Inspector ID is required'),
  affiliation: z.string().optional(),
  inspectionDateTime: z.string().min(1, 'Inspection date/time is required'),
  areasInspected: areasInspectedSchema,
})

/**
 * Building section validation
 */
export const buildingSectionSchema = z.object({
  address1: z.string().min(1, 'Address is required'),
  address2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  county: z.string().min(1, 'County is required'),
  state: z.string().min(1, 'State is required'),
  zipcode: z.string().optional(),
  typeOfConstruction: constructionTypeSchema,
  primaryOccupancy: occupancyTypeSchema,
  contactPhone: z.string().optional(),
  storiesAboveGround: z.string().optional(),
  storiesBelowGround: z.string().optional(),
  buildingFootprint: z.string().optional(),
  residentialUnits: z.string().optional(),
  inhabitableResidentialUnits: z.string().optional(),
})

/**
 * Evaluation section validation
 */
export const evaluationSectionSchema = z.object({
  hazards: hazardAssessmentSchema,
  estimatedBuildingDamage: damagePercentageSchema,
  comments: z.string().optional(),
})

/**
 * Posting section validation
 */
export const postingSectionSchema = z.object({
  postingStatus: postingStatusSchema.optional(),
  restrictionsText: z.string().optional(),
  detailedEvalRecommended: z.boolean(),
  detailedEvalTypes: z.array(detailedEvalTypeSchema).optional(),
})

/**
 * Image section validation (no required fields)
 */
export const imageSectionSchema = z.object({
  photos: z.array(assessmentPhotoSchema).optional(),
})
